import { PulseemReactInstance } from './PulseemReactAPI';

// "Go to Dialer" side-menu item (ETX voice dialer SSO).
// The server decides everything from the session's JWT; the client sends no identifiers.
// The returned URL carries a one-time ticket in its fragment (#t=): never log it or store it.

const STATUS_URL = 'voice/sso-status';
const TICKET_URL = 'voice/sso-token';
const REQUEST_TIMEOUT_MS = 15000;

export const VOICE_DIALER_ERROR = 'master.voiceDialerError';
export const VOICE_DIALER_POPUP_BLOCKED = 'master.voiceDialerPopupBlocked';
export const VOICE_DIALER_2FA_REQUIRED = 'master.voiceDialer2faRequired';

// The sidebar is rebuilt on every page, so the answer is kept for the life of the page
// (switching account is a full reload). A failure hides the item until the next reload.
let enabledCache: boolean | null = null;
let enabledRequest: Promise<boolean> | null = null;
let ticketInFlight = false;

// The axios timeout does not cover the RefreshToken.ashx call that the request interceptor
// awaits first, so the whole call is bounded here.
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

export const getCachedVoiceDialerEnabled = (): boolean => enabledCache === true;

// The side-menu item is added here, after getRoutes(), instead of inside routes.tsx: release branches
// keep appending parameters and items to getRoutes, and an edit there conflicts on every merge.
export const withVoiceDialerRoute = (
  routes: any[],
  t: (key: string) => any,
  isEnabled: boolean,
  onClick: () => void
): any[] => {
  if (!isEnabled) return routes;
  const item = {
    key: 'voiceDialer',
    title: t('master.voiceDialer'),
    pageTitle: t('master.voiceDialer'),
    isShow: true,
    iconName: 'MdPhoneInTalk',
    onClick,
  };
  const integrationsIndex = routes.findIndex((route: any) => route?.key === 'Integrations');
  if (integrationsIndex < 0) return [...routes, item];
  return [...routes.slice(0, integrationsIndex + 1), item, ...routes.slice(integrationsIndex + 1)];
};

export const getVoiceDialerEnabled = (): Promise<boolean> => {
  if (enabledCache !== null) return Promise.resolve(enabledCache);
  if (!enabledRequest) {
    enabledRequest = withTimeout(PulseemReactInstance.get(STATUS_URL), REQUEST_TIMEOUT_MS)
      .then((response: any) => response?.data?.StatusCode === 200 && response?.data?.Data?.Enabled === true)
      .catch(() => false)
      .then((enabled: boolean) => {
        enabledCache = enabled;
        return enabled;
      });
  }
  return enabledRequest;
};

export const openVoiceDialer = async (onError: (messageKey: string) => void): Promise<void> => {
  if (ticketInFlight) return;

  // Open the tab synchronously, inside the click and before any await, or popup blockers stop it.
  // No 'noopener' feature here: with it window.open returns null and the tab could not be navigated.
  const dialerWindow = window.open('', '_blank');
  if (!dialerWindow) {
    onError(VOICE_DIALER_POPUP_BLOCKED);
    return;
  }
  try {
    dialerWindow.opener = null;
  } catch (e) {
    // ignore
  }

  ticketInFlight = true;
  try {
    const response: any = await withTimeout(PulseemReactInstance.post(TICKET_URL, {}), REQUEST_TIMEOUT_MS);
    const body = response?.data;
    const url = body?.StatusCode === 200 ? body?.Data?.Url : null;
    if (typeof url !== 'string' || url.indexOf('https://') !== 0 || url.indexOf('/sso#t=') < 0) {
      throw body;
    }
    if (dialerWindow.closed) return;
    dialerWindow.location.replace(url);
  } catch (error: any) {
    const closedByUser = dialerWindow.closed;
    try {
      dialerWindow.close();
    } catch (e) {
      // ignore
    }
    if (!closedByUser) {
      onError(error?.Message === 'no_2fa' ? VOICE_DIALER_2FA_REQUIRED : VOICE_DIALER_ERROR);
    }
  } finally {
    ticketInFlight = false;
  }
};
